import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import SEO from '../components/common/SEO';
import { useAuth } from '../hooks/useAuth';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user: authUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const returnTo = location.state?.returnTo || null;

  // Redirect if already authenticated — send to role-based dashboard
  if (isAuthenticated && authUser) {
    const dest = returnTo ?? 
      (authUser.role === 'consumer' ? '/consumer/dashboard' : 
       authUser.role === 'business_owner' ? '/business/dashboard' : '/');
    navigate(dest, { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        const dest = returnTo ?? 
          (result.user?.role === 'consumer' ? '/consumer/dashboard' : 
           result.user?.role === 'business_owner' ? '/business/dashboard' : '/');
        navigate(dest, { replace: true });
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-62px)] bg-slate-100 px-4 py-6 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
      <SEO
        title="Sign In to VMC | Access Your Consumer or Business Dashboard"
        description="Log in to your VMC account to manage your business profile, respond to reviews, or continue your search for trusted services."
      />
      <div className="mx-auto flex min-h-[78vh] w-full max-w-7xl overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-300/35 ring-1 ring-slate-200 lg:min-h-[82vh]">
        <section className="relative hidden w-3/5 overflow-hidden bg-[#0F172A] p-10 text-white lg:flex lg:flex-col xl:p-14">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-24 -top-20 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
            <div className="absolute -right-20 bottom-12 h-72 w-72 rounded-full bg-sky-500/10 blur-3xl" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.07),transparent_40%),radial-gradient(circle_at_85%_80%,rgba(91,79,207,0.2),transparent_45%)]" />
          </div>

          <div className="relative z-10 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-lg shadow-indigo-900/40">
              <span className="text-sm font-black tracking-tight">VMC</span>
            </div>
            <div className="leading-none">
              <p className="text-base font-bold tracking-tight">VMC Reviews</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-300">Business Platform</p>
            </div>
          </div>

          <div className="relative z-10 mt-20 max-w-lg xl:mt-24">
            <h1 className="text-5xl font-extrabold leading-[1.1] tracking-tight">Welcome Back</h1>
            <p className="mt-5 max-w-md text-base leading-7 text-slate-300">
              Sign in to continue building trust through transparent reviews.
            </p>

            <div className="mt-8 space-y-4">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-200">Your Access Points:</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="font-medium text-indigo-300">For Consumers</p>
                  <p className="mt-1 text-sm text-slate-400">Log in to write reviews, save your favorite businesses, and track your community contributions.</p>
                </div>
                <div>
                  <p className="font-medium text-indigo-300">For Businesses</p>
                  <p className="mt-1 text-sm text-slate-400">Access your Reputation Console to manage your listing, respond to customer feedback, and view your trust insights.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative z-10 mt-auto grid grid-cols-3 gap-3 pt-10">
            <div className="flex min-w-0 items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-sm">
              <svg className="h-5 w-5 text-indigo-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5V4H2v16h5m10 0v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6m10 0H7" />
              </svg>
              <span className="text-[13px] font-medium leading-tight text-slate-100">10,000+ Businesses</span>
            </div>
            <div className="flex min-w-0 items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-sm">
              <svg className="h-5 w-5 text-indigo-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.5 2A8.5 8.5 0 113.5 12a8.5 8.5 0 0117 0z" />
              </svg>
              <span className="text-[13px] font-medium leading-tight text-slate-100">Verified Reviews</span>
            </div>
            <div className="flex min-w-0 items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-sm">
              <svg className="h-5 w-5 text-indigo-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l8 4v6c0 5-3.5 8-8 8s-8-3-8-8V7l8-4z" />
              </svg>
              <span className="text-[13px] font-medium leading-tight text-slate-100">Secure Platform</span>
            </div>
          </div>
        </section>

        <section className="flex w-full items-center bg-white px-5 py-10 sm:px-8 md:px-10 lg:w-2/5 lg:px-12 xl:px-14">
          <div className="mx-auto w-full max-w-md">
            <div className="mb-8 lg:hidden">
              <div className="flex items-center gap-2.5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-md shadow-indigo-300/50">
                  <span className="text-sm font-black tracking-tight text-white">VMC</span>
                </div>
                <div>
                  <p className="text-[15px] font-bold tracking-tight text-slate-900">VMC Reviews</p>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Business Platform</p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold tracking-tight text-slate-900">Login to your account</h2>
            <p className="mt-2 text-sm text-slate-500">Use your credentials to access your dashboard.</p>

            {returnTo && (
              <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                ℹ️ Please login to access that page.
              </div>
            )}

            {error && (
              <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-[#E2E8F0] px-4 py-[14px] text-[15px] text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#5B4FCF] focus:shadow-[0_0_0_3px_rgba(91,79,207,0.15)]"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-[#E2E8F0] px-4 py-[14px] text-[15px] text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#5B4FCF] focus:shadow-[0_0_0_3px_rgba(91,79,207,0.15)]"
                  placeholder="********"
                  required
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-slate-600">
                  <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                  <span>Remember me</span>
                </label>
                <Link to="/forgot-password" className="font-medium text-[#5B4FCF] transition-colors hover:text-[#4A3FC2]">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-gradient-to-r from-[#5B4FCF] to-[#7C6FE0] px-4 py-[14px] text-[15px] font-semibold text-white shadow-md shadow-indigo-200 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-indigo-300/50 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>

            <div className="mt-8 text-center text-sm text-slate-600">
              <p>
                Don't have an account?{' '}
                <Link to="/register" className="font-semibold text-[#5B4FCF] transition-colors hover:text-[#4A3FC2]">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LoginPage;
